import { getSupabase, SUPABASE_TABLE_NAME, SUPABASE_ROW_ID } from './supabase';
import { getApiUrl } from './api';
import { matchesOutlet } from '../data/adminAccounts';
import { OutletScheduleConfig, OutletScheduleMode, OutletOperatingStatus } from '../types';

export const OUTLET_AVAILABILITY_STORAGE_KEY = 'leton_outlet_availability_status';
export const OUTLET_SCHEDULES_STORAGE_KEY = 'leton_outlet_schedules_status';
export const OUTLET_AVAILABILITY_EVENT = 'leton_outlet_availability_updated';

/**
 * Standardize outlet ID to match 'sudirman', 'kelakap_7', or 'letgo-mpp'
 */
export function normalizeOutletKey(outletId: string): string {
  const clean = String(outletId || '').toLowerCase().trim();
  if (clean === 'sudirman' || clean.includes('sudirman') || clean === 'chapter-5') {
    return 'sudirman';
  }
  if (
    clean === 'kelakap_7' ||
    clean === 'kelakap' ||
    clean === 'ratusima' ||
    clean.includes('kelakap') ||
    clean.includes('ratusima') ||
    clean === 'chapter-6'
  ) {
    return 'kelakap_7';
  }
  if (clean.includes('letgo') || clean.includes('mpp')) {
    return 'letgo-mpp';
  }
  return clean;
}

// Canonical Default Schedules (WIB / Asia/Jakarta)
export const DEFAULT_OUTLET_SCHEDULES: Record<string, OutletScheduleConfig> = {
  sudirman: {
    mode: 'SCHEDULE',
    openTime: '08:00',
    closeTime: '00:00',
  },
  kelakap_7: {
    mode: 'SCHEDULE',
    openTime: '08:00',
    closeTime: '23:00',
  },
  'letgo-mpp': {
    mode: 'SCHEDULE',
    openTime: '07:00',
    closeTime: '17:00',
  },
};

// In-memory & local storage cache
const localStatusCache: Record<string, boolean> = {
  sudirman: true,
  kelakap_7: true,
  'letgo-mpp': true,
};

const localSchedulesCache: Record<string, OutletScheduleConfig> = {
  ...DEFAULT_OUTLET_SCHEDULES,
};

// Initialize cache from localStorage
if (typeof window !== 'undefined') {
  try {
    const rawAvail = localStorage.getItem(OUTLET_AVAILABILITY_STORAGE_KEY);
    if (rawAvail) {
      const parsed = JSON.parse(rawAvail);
      if (parsed && typeof parsed === 'object') {
        Object.assign(localStatusCache, parsed);
      }
    }
  } catch {}

  try {
    const rawSched = localStorage.getItem(OUTLET_SCHEDULES_STORAGE_KEY);
    if (rawSched) {
      const parsed = JSON.parse(rawSched);
      if (parsed && typeof parsed === 'object') {
        Object.assign(localSchedulesCache, parsed);
      }
    }
  } catch {}
}

/**
 * Get current minutes from midnight in Asia/Jakarta (WIB / UTC+7) timezone
 */
export function getCurrentWibMinutes(): number {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Jakarta',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const hStr = parts.find((p) => p.type === 'hour')?.value || '00';
    const mStr = parts.find((p) => p.type === 'minute')?.value || '00';
    const hour = parseInt(hStr, 10) || 0;
    const minute = parseInt(mStr, 10) || 0;
    return hour * 60 + minute;
  } catch {
    // Fallback: UTC + 7 hours
    const d = new Date();
    const utcHours = d.getUTCHours();
    const utcMinutes = d.getUTCMinutes();
    const wibHours = (utcHours + 7) % 24;
    return wibHours * 60 + utcMinutes;
  }
}

/**
 * Parse time string "HH:mm" into minutes from 00:00
 */
export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const clean = timeStr.trim();
  const [h, m] = clean.split(':').map((v) => parseInt(v, 10) || 0);
  return h * 60 + (m || 0);
}

/**
 * Centralized Outlet Operating Status Resolver
 * Determines whether an outlet is OPEN or CLOSED based on:
 * 1. Mode CLOSED -> forced CLOSED
 * 2. Mode OPEN -> forced OPEN
 * 3. Mode SCHEDULE -> evaluates against Asia/Jakarta (WIB) current time supporting overnight/midnight schedules
 */
export function resolveOutletOperatingStatus(
  outletId: string,
  schedules?: Record<string, OutletScheduleConfig> | null,
  manualAvailability?: Record<string, boolean> | null,
  customWibMinutes?: number
): OutletOperatingStatus {
  const normKey = normalizeOutletKey(outletId);
  const activeSchedules = schedules || localSchedulesCache;
  const schedule: OutletScheduleConfig = activeSchedules[normKey] || DEFAULT_OUTLET_SCHEDULES[normKey] || {
    mode: 'SCHEDULE',
    openTime: '08:00',
    closeTime: '23:00',
  };

  const mode: OutletScheduleMode = schedule.mode || 'SCHEDULE';
  const openTime = schedule.openTime || '08:00';
  const closeTime = schedule.closeTime || '23:00';

  // 1. Manual CLOSED overrides everything
  if (mode === 'CLOSED') {
    return {
      isOpen: false,
      mode: 'CLOSED',
      openTime,
      closeTime,
      nextOpenTime: `Buka sesuai jadwal (${openTime} WIB)`,
      reason: 'Outlet ditutup sementara oleh Admin.',
      statusBadge: 'Tutup Manual',
    };
  }

  // 2. Manual OPEN overrides schedule
  if (mode === 'OPEN') {
    return {
      isOpen: true,
      mode: 'OPEN',
      openTime,
      closeTime,
      reason: 'Outlet dibuka manual oleh Admin.',
      statusBadge: 'Buka Manual',
    };
  }

  // 3. Fallback to manualAvailability if schedule was not set and manual is false
  if (manualAvailability && manualAvailability[normKey] === false && !activeSchedules[normKey]) {
    return {
      isOpen: false,
      mode: 'CLOSED',
      openTime,
      closeTime,
      nextOpenTime: `Buka pukul ${openTime} WIB`,
      reason: 'Tidak menerima pesanan saat ini.',
      statusBadge: 'Tutup',
    };
  }

  // 4. Mode: SCHEDULE -> evaluate based on current Asia/Jakarta (WIB) time
  const currMin = customWibMinutes !== undefined ? customWibMinutes : getCurrentWibMinutes();
  const openMin = parseTimeToMinutes(openTime);
  const closeMin = parseTimeToMinutes(closeTime);

  let isOpen = false;

  // Handle midnight close (00:00 or 24:00) when openMin > 0 (e.g. 08:00 - 00:00)
  if ((closeTime === '00:00' || closeTime === '24:00' || closeMin === 0) && openMin > 0) {
    isOpen = currMin >= openMin && currMin < 1440;
  } else if (closeMin > openMin) {
    // Standard daytime: e.g. 08:00 - 23:00
    isOpen = currMin >= openMin && currMin < closeMin;
  } else if (closeMin < openMin) {
    // Overnight past midnight: e.g. 18:00 - 02:00
    isOpen = currMin >= openMin || currMin < closeMin;
  } else {
    // openMin === closeMin -> 24 hours open
    isOpen = true;
  }

  return {
    isOpen,
    mode: 'SCHEDULE',
    openTime,
    closeTime,
    nextOpenTime: !isOpen ? `Buka pukul ${openTime} WIB` : undefined,
    reason: isOpen ? 'Sesuai jam operasional' : `Di luar jam operasional (${openTime} – ${closeTime} WIB)`,
    statusBadge: isOpen ? 'Buka' : 'Tutup',
  };
}

/**
 * Extract availability status map and schedules from LetonData content
 */
export function extractAvailabilityFromContent(content: any): {
  availabilityMap: Record<string, boolean>;
  schedulesMap: Record<string, OutletScheduleConfig>;
} {
  const availabilityMap: Record<string, boolean> = {
    sudirman: true,
    kelakap_7: true,
    'letgo-mpp': true,
  };

  const schedulesMap: Record<string, OutletScheduleConfig> = {
    ...DEFAULT_OUTLET_SCHEDULES,
  };

  if (!content || typeof content !== 'object') {
    return { availabilityMap, schedulesMap };
  }

  // 1. Extract outletSchedules if present
  if (content.outletSchedules && typeof content.outletSchedules === 'object') {
    Object.keys(content.outletSchedules).forEach((key) => {
      const norm = normalizeOutletKey(key);
      const conf = content.outletSchedules[key];
      if (conf && typeof conf === 'object') {
        schedulesMap[norm] = {
          mode: conf.mode || 'SCHEDULE',
          openTime: conf.openTime || DEFAULT_OUTLET_SCHEDULES[norm]?.openTime || '08:00',
          closeTime: conf.closeTime || DEFAULT_OUTLET_SCHEDULES[norm]?.closeTime || '23:00',
        };
      }
    });
  }

  // 2. Direct outletAvailability mapping object if present
  if (content.outletAvailability && typeof content.outletAvailability === 'object') {
    Object.keys(content.outletAvailability).forEach((key) => {
      const norm = normalizeOutletKey(key);
      availabilityMap[norm] = content.outletAvailability[key] !== false;
    });
  }

  // 3. Extract from branches array
  if (Array.isArray(content.branches)) {
    content.branches.forEach((branch: any) => {
      if (branch && branch.id) {
        const norm = normalizeOutletKey(branch.id);
        if (branch.accepting_orders !== undefined) {
          availabilityMap[norm] = branch.accepting_orders !== false;
        }
      }
    });
  }

  // 4. Extract from mobileService object
  if (content.mobileService && typeof content.mobileService === 'object') {
    if (content.mobileService.accepting_orders !== undefined) {
      availabilityMap['letgo-mpp'] = content.mobileService.accepting_orders !== false;
    }
  }

  // 5. Derive effective availability boolean from resolveOutletOperatingStatus for each outlet
  Object.keys(schedulesMap).forEach((normKey) => {
    const status = resolveOutletOperatingStatus(normKey, schedulesMap, availabilityMap);
    availabilityMap[normKey] = status.isOpen;
  });

  return { availabilityMap, schedulesMap };
}

/**
 * Fetch all outlets availability and schedules directly from production source (public.leton_content)
 */
export async function fetchOutletsAvailability(): Promise<Record<string, boolean>> {
  const result: Record<string, boolean> = { ...localStatusCache };

  try {
    const client = getSupabase();
    const { data, error } = await client
      .from(SUPABASE_TABLE_NAME)
      .select('content')
      .eq('id', SUPABASE_ROW_ID)
      .maybeSingle();

    if (!error && data && data.content) {
      const { availabilityMap, schedulesMap } = extractAvailabilityFromContent(data.content);
      Object.assign(result, availabilityMap);
      Object.assign(localStatusCache, result);
      Object.assign(localSchedulesCache, schedulesMap);

      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(OUTLET_AVAILABILITY_STORAGE_KEY, JSON.stringify(result));
          localStorage.setItem(OUTLET_SCHEDULES_STORAGE_KEY, JSON.stringify(schedulesMap));
          window.dispatchEvent(
            new CustomEvent(OUTLET_AVAILABILITY_EVENT, {
              detail: { availability: result, schedules: schedulesMap },
            })
          );
        } catch {}
      }
      return result;
    }
  } catch (err) {
    console.warn('[fetchOutletsAvailability Supabase Notice]:', err);
  }

  // Fallback: Query backend server API /api/outlets/status
  try {
    const isSameHost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('.run.app'));

    if (isSameHost) {
      const res = await fetch(getApiUrl('/api/outlets/status'));
      if (res.ok) {
        const parsed = await res.json().catch(() => null);
        if (parsed && parsed.success && parsed.data) {
          Object.assign(result, parsed.data);
          Object.assign(localStatusCache, result);
          if (parsed.schedules) {
            Object.assign(localSchedulesCache, parsed.schedules);
          }
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem(OUTLET_AVAILABILITY_STORAGE_KEY, JSON.stringify(result));
              if (parsed.schedules) {
                localStorage.setItem(OUTLET_SCHEDULES_STORAGE_KEY, JSON.stringify(parsed.schedules));
              }
              window.dispatchEvent(
                new CustomEvent(OUTLET_AVAILABILITY_EVENT, {
                  detail: { availability: result, schedules: localSchedulesCache },
                })
              );
            } catch {}
          }
          return result;
        }
      }
    }
  } catch (apiErr) {
    console.warn('[fetchOutletsAvailability API Notice]:', apiErr);
  }

  return result;
}

/**
 * Fetch all outlet schedules
 */
export function getLocalOutletSchedules(): Record<string, OutletScheduleConfig> {
  return { ...localSchedulesCache };
}

/**
 * Check if a specific outlet is currently open and accepting orders (synchronous lookup)
 */
export function isOutletAcceptingOrdersSync(outletId: string): boolean {
  const normKey = normalizeOutletKey(outletId);
  const status = resolveOutletOperatingStatus(normKey, localSchedulesCache, localStatusCache);
  return status.isOpen;
}

/**
 * Update outlet schedule and mode (Super Admin only for schedule changes, Outlet Admin for manual override)
 */
export async function updateOutletSchedule(
  outletId: string,
  scheduleConfig: Partial<OutletScheduleConfig>,
  adminRole?: string,
  adminOutletId?: string
): Promise<{ success: boolean; error?: string }> {
  const normKey = normalizeOutletKey(outletId);

  // RBAC check: Outlet Admin can only toggle their assigned outlet
  if (adminRole === 'outlet_admin') {
    if (!adminOutletId || !matchesOutlet(outletId, adminOutletId)) {
      return {
        success: false,
        error: 'Akses Ditolak: Anda hanya memiliki wewenang untuk mengatur outlet Anda sendiri.',
      };
    }
  }

  const currentConf = localSchedulesCache[normKey] || DEFAULT_OUTLET_SCHEDULES[normKey] || {
    mode: 'SCHEDULE',
    openTime: '08:00',
    closeTime: '23:00',
  };

  const updatedConfig: OutletScheduleConfig = {
    ...currentConf,
    ...scheduleConfig,
  };

  localSchedulesCache[normKey] = updatedConfig;
  const operatingStatus = resolveOutletOperatingStatus(normKey, localSchedulesCache, localStatusCache);
  localStatusCache[normKey] = operatingStatus.isOpen;

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(OUTLET_SCHEDULES_STORAGE_KEY, JSON.stringify(localSchedulesCache));
      localStorage.setItem(OUTLET_AVAILABILITY_STORAGE_KEY, JSON.stringify(localStatusCache));
      window.dispatchEvent(
        new CustomEvent(OUTLET_AVAILABILITY_EVENT, {
          detail: { availability: localStatusCache, schedules: localSchedulesCache },
        })
      );
    } catch {}
  }

  // 1. Persist to Backend Server API
  let backendSuccess = false;
  let backendError: string | undefined;

  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (adminRole) headers['x-admin-role'] = adminRole;
    if (adminOutletId) headers['x-outlet-id'] = adminOutletId;

    const res = await fetch(getApiUrl(`/api/admin/outlets/${normKey}/schedule`), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        outletId: normKey,
        schedule: updatedConfig,
        accepting_orders: operatingStatus.isOpen,
      }),
    });

    const parsed = await res.json().catch(() => null);
    if (res.ok && parsed && parsed.success) {
      backendSuccess = true;
    } else {
      backendError = parsed?.error || `Server responded with status ${res.status}`;
    }
  } catch (apiErr: any) {
    backendError = apiErr?.message || 'Gagal menghubungi server.';
  }

  // 2. Persist directly to Supabase table public.leton_content
  try {
    const client = getSupabase(adminRole, adminOutletId);
    const { data: existingRow } = await client
      .from(SUPABASE_TABLE_NAME)
      .select('content')
      .eq('id', SUPABASE_ROW_ID)
      .maybeSingle();

    if (existingRow && existingRow.content) {
      const updatedContent = { ...existingRow.content };

      // Update outletSchedules map
      updatedContent.outletSchedules = {
        ...(updatedContent.outletSchedules || {}),
        [normKey]: updatedConfig,
      };

      // Update outletAvailability map for backward compatibility
      updatedContent.outletAvailability = {
        ...(updatedContent.outletAvailability || {}),
        [normKey]: operatingStatus.isOpen,
      };

      // Update in branches array if matching
      if (Array.isArray(updatedContent.branches)) {
        updatedContent.branches = updatedContent.branches.map((b: any) => {
          if (normalizeOutletKey(b.id) === normKey) {
            return { ...b, accepting_orders: operatingStatus.isOpen };
          }
          return b;
        });
      }

      // Update in mobileService if matching letgo-mpp
      if (normKey === 'letgo-mpp' && updatedContent.mobileService) {
        updatedContent.mobileService = {
          ...updatedContent.mobileService,
          accepting_orders: operatingStatus.isOpen,
        };
      }

      await client
        .from(SUPABASE_TABLE_NAME)
        .update({
          content: updatedContent,
          updated_at: new Date().toISOString(),
        })
        .eq('id', SUPABASE_ROW_ID);
    }
  } catch (sbErr) {
    console.warn('[updateOutletSchedule Supabase Notice]:', sbErr);
  }

  if (backendSuccess) {
    return { success: true };
  }

  return {
    success: true,
    error: backendError,
  };
}

/**
 * Backward-compatible helper: Update outlet accepting orders status (Admin toggle)
 */
export async function updateOutletAvailability(
  outletId: string,
  accepting_orders: boolean,
  adminRole?: string,
  adminOutletId?: string
): Promise<{ success: boolean; error?: string }> {
  // If explicitly toggled true -> mode: 'OPEN', false -> mode: 'CLOSED'
  const newMode: OutletScheduleMode = accepting_orders ? 'OPEN' : 'CLOSED';
  return updateOutletSchedule(outletId, { mode: newMode }, adminRole, adminOutletId);
}

/**
 * Subscribe to realtime outlet availability and schedule changes from public.leton_content
 */
export function subscribeToOutletAvailabilityRealtime(
  onUpdate: (
    statusMap: Record<string, boolean>,
    schedulesMap: Record<string, OutletScheduleConfig>
  ) => void
): () => void {
  let isSubscribed = true;

  // 1. Initial trigger with cache
  onUpdate({ ...localStatusCache }, { ...localSchedulesCache });

  // 2. Fetch fresh data
  fetchOutletsAvailability().then(() => {
    if (isSubscribed) {
      onUpdate({ ...localStatusCache }, { ...localSchedulesCache });
    }
  });

  // 3. Custom window event listener (in-tab & cross-component)
  const handleCustomEvent = (e: any) => {
    if (isSubscribed) {
      onUpdate({ ...localStatusCache }, { ...localSchedulesCache });
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener(OUTLET_AVAILABILITY_EVENT, handleCustomEvent);
    window.addEventListener('storage', (e) => {
      if (
        (e.key === OUTLET_AVAILABILITY_STORAGE_KEY || e.key === OUTLET_SCHEDULES_STORAGE_KEY) &&
        isSubscribed
      ) {
        onUpdate({ ...localStatusCache }, { ...localSchedulesCache });
      }
    });
  }

  // 4. Time minute-ticker: automatically re-evaluate schedule every 30 seconds
  const ticker = setInterval(() => {
    if (!isSubscribed) return;
    let changed = false;
    Object.keys(localSchedulesCache).forEach((key) => {
      const status = resolveOutletOperatingStatus(key, localSchedulesCache, localStatusCache);
      if (localStatusCache[key] !== status.isOpen) {
        localStatusCache[key] = status.isOpen;
        changed = true;
      }
    });
    if (changed) {
      onUpdate({ ...localStatusCache }, { ...localSchedulesCache });
    }
  }, 30000);

  // 5. Fallback Polling Control
  let pollInterval: ReturnType<typeof setInterval> | null = null;
  const startFallbackPolling = () => {
    if (!isSubscribed || pollInterval !== null) return;
    pollInterval = setInterval(() => {
      if (isSubscribed) {
        fetchOutletsAvailability().then(() => {
          if (isSubscribed) {
            onUpdate({ ...localStatusCache }, { ...localSchedulesCache });
          }
        });
      }
    }, 15000);
  };
  const stopFallbackPolling = () => {
    if (pollInterval !== null) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  // 6. Supabase Realtime channel subscription on 'leton_content' table
  let supabaseChannel: any = null;
  try {
    const client = getSupabase();
    supabaseChannel = client
      .channel('leton_content_outlet_availability_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: SUPABASE_TABLE_NAME,
        },
        (payload: any) => {
          if (payload.new && payload.new.content && isSubscribed) {
            const { availabilityMap, schedulesMap } = extractAvailabilityFromContent(payload.new.content);
            Object.assign(localStatusCache, availabilityMap);
            Object.assign(localSchedulesCache, schedulesMap);
            if (typeof window !== 'undefined') {
              try {
                localStorage.setItem(OUTLET_AVAILABILITY_STORAGE_KEY, JSON.stringify(localStatusCache));
                localStorage.setItem(OUTLET_SCHEDULES_STORAGE_KEY, JSON.stringify(localSchedulesCache));
              } catch {}
            }
            onUpdate({ ...localStatusCache }, { ...localSchedulesCache });
          }
        }
      )
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          stopFallbackPolling();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          startFallbackPolling();
        }
      });
  } catch (rtErr) {
    console.warn('[subscribeToOutletAvailabilityRealtime Notice]:', rtErr);
    startFallbackPolling();
  }

  return () => {
    isSubscribed = false;
    clearInterval(ticker);
    stopFallbackPolling();
    if (typeof window !== 'undefined') {
      window.removeEventListener(OUTLET_AVAILABILITY_EVENT, handleCustomEvent);
    }
    if (supabaseChannel) {
      try {
        const client = getSupabase();
        client.removeChannel(supabaseChannel);
      } catch {}
    }
  };
}
